import { BaseSyntheticEvent } from 'react';
import { format, formatISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

import useRescheduleScheduledMessageMutation from 'components/domain/automation/scheduledMessages/useRescheduleScheduledMessageMutation';
import useNotify from 'hooks/useNotify';
import { BaseErrorResponse } from 'models/_base';
import { ScheduledMessage } from 'models/automation/ScheduledMessages';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { TIMEZONES } from 'utils/constants/timezones';
import { getTimezoneOffset } from 'utils/timezones';
import { flushSync } from 'react-dom';
import { FormContextParts } from 'components/forms/form/Form.types';
import { ScheduledMessagesRescheduleModalFormValues } from './ScheduledMessagesRescheduleModal.schema';

const useScheduledMessageRescheduleModalFormSubmit = ({
  scheduledMessage,
  closeModal,
}: {
  scheduledMessage: ScheduledMessage;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useNotify();

  const onError = (error: BaseErrorResponse) => {
    notifyError(
      getErrorMessage({
        apiErrorCode: error.response?.data?.apiErrorCode,
        apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
        baseKey: 'pageAutomations.triggers.apiErrorCodes.',
        t,
      }),
    );
  };

  const { mutateAsync: rescheduleScheduledMessage } =
    useRescheduleScheduledMessageMutation();

  const onSubmit = async (
    formValues: ScheduledMessagesRescheduleModalFormValues,
    _event: BaseSyntheticEvent | undefined,
    { reset }: FormContextParts<ScheduledMessagesRescheduleModalFormValues>,
  ) => {
    const { date, time, timezone } = formValues;

    try {
      const { offsetISO } = getTimezoneOffset({
        timezone: timezone as keyof typeof TIMEZONES,
      });
      const dateString = formatISO(date, { representation: 'date' });
      const timeString = format(time, 'HH:mm:00.000');

      await rescheduleScheduledMessage(
        {
          newFireTime: `${dateString}T${timeString}${offsetISO}`,
          scheduledMessageUid: scheduledMessage.uid,
        },
        {
          onSuccess: () => {
            // flushSync ensures the form is reset synchronously before
            // closeModal() triggers navigation. Without it, the dirty checker
            // would still see a dirty form during navigation and prompt the
            // user with an unsaved-changes confirmation.
            flushSync(() => reset(formValues, { keepDirty: false }));
            closeModal();
            notifySuccess(
              t('pageAutomations.scheduledMessages.modalReschedule.success'),
            );
          },
        },
      );
    } catch (error) {
      onError(error as BaseErrorResponse);
    }
  };

  return { onSubmit };
};

export default useScheduledMessageRescheduleModalFormSubmit;
